/**
 * Shared country-list extras and Settings deletions.
 * Unused catalog countries stay hidden after reload; work-site countries always remain visible.
 */
import { ref } from 'vue';
import { countriesMatch, normalizeCountry } from './countryCatalog.js';

const STORAGE_KEY = 'crc.hr.suppressedCountries.v1';

export const extraCountriesRef = ref([]);
export const suppressedCountriesRef = ref(readStoredSuppressed());
/** Rows from POST countries/manage action list. Empty until the catalog loads. */
export const catalogCountriesRef = ref([]);
export const countryCatalogLoadedRef = ref(false);

let catalogPromise = null;

export function catalogCountryNames() {
  return catalogCountriesRef.value
    .map((row) => normalizeCountry(row?.name))
    .filter(Boolean);
}

export function replaceCountryCatalog(rows) {
  catalogCountriesRef.value = Array.isArray(rows) ? rows : [];
  countryCatalogLoadedRef.value = true;
}

/**
 * Options for buildCountryOptions. A loaded catalog replaces the hardcoded
 * Moldova/US/UK/Spain list so a country removed in Settings stays removed.
 */
export function countryCatalogListOptions(extra = []) {
  const names = catalogCountryNames();
  return {
    extra: [...names, ...extraCountriesRef.value, ...(Array.isArray(extra) ? extra : [])],
    includeCanonical: !countryCatalogLoadedRef.value,
    exclude: suppressedCountriesRef.value,
  };
}

export async function loadCountryCatalog({ force = false } = {}) {
  if (!force && countryCatalogLoadedRef.value) {
    return catalogCountriesRef.value;
  }
  if (!force && catalogPromise) {
    return catalogPromise;
  }
  catalogPromise = import('@/api/countriesApi.js')
    .then(({ countriesApi }) => countriesApi.list())
    .then((data) => {
      const rows = Array.isArray(data?.countries) ? data.countries : [];
      replaceCountryCatalog(rows);
      return catalogCountriesRef.value;
    })
    .finally(() => {
      catalogPromise = null;
    });
  return catalogPromise;
}

function readStoredSuppressed() {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set();
    const values = [];
    parsed.forEach((item) => {
      const canonical = normalizeCountry(item);
      const key = canonical.toLowerCase();
      if (!canonical || seen.has(key)) return;
      seen.add(key);
      values.push(canonical);
    });
    return values;
  } catch {
    return [];
  }
}

function persistSuppressed() {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(suppressedCountriesRef.value));
  } catch {
    // Private mode / quota — keep in-memory list for this session.
  }
}

function withoutCountry(list, value) {
  return list.filter((item) => !countriesMatch(item, value));
}

export function rememberExtraCountry(value) {
  const canonical = normalizeCountry(value);
  if (!canonical) return '';
  extraCountriesRef.value = [...withoutCountry(extraCountriesRef.value, canonical), canonical];
  restoreSuppressedCountry(canonical);
  return canonical;
}

export function restoreSuppressedCountry(value) {
  const canonical = normalizeCountry(value);
  if (!canonical) return;
  if (!suppressedCountriesRef.value.some((item) => countriesMatch(item, canonical))) return;
  suppressedCountriesRef.value = withoutCountry(suppressedCountriesRef.value, canonical);
  persistSuppressed();
}

export function suppressCountry(value) {
  const canonical = normalizeCountry(value);
  if (!canonical) return;
  extraCountriesRef.value = withoutCountry(extraCountriesRef.value, canonical);
  if (suppressedCountriesRef.value.some((item) => countriesMatch(item, canonical))) return;
  suppressedCountriesRef.value = [...suppressedCountriesRef.value, canonical];
  persistSuppressed();
}
